'use client';

import { useCallback } from 'react';

import {
  SESSION_STORE_KEYS,
  useSessionStore,
} from '@workspace/lib/hooks/store/use-session-store';
import type { PrimitiveResponseValue } from '@workspace/lib/types/custom-schema-propeties-definitions';

type VisibilityRules = Record<string, PrimitiveResponseValue>;

export type SetVisibilityRule = ReturnType<
  typeof useVisibilityRules
>['setVisibilityRule'];

export type GetVisibilityRule = ReturnType<
  typeof useVisibilityRules
>['getVisibilityRuleValue'];

export function useVisibilityRules() {
  const { getFromSessionStore, saveToSessionStore } = useSessionStore();

  const setVisibilityRule = useCallback(
    (key: string, value: PrimitiveResponseValue) => {
      const visibilityRules =
        (getFromSessionStore(SESSION_STORE_KEYS.visibilityRules) as
          VisibilityRules | undefined) ?? {};

      saveToSessionStore(SESSION_STORE_KEYS.visibilityRules, {
        ...visibilityRules,
        [key]: value,
      });
      return;
    },
    [saveToSessionStore, getFromSessionStore],
  );

  const getVisibilityRuleValue = useCallback(
    (key: string) => {
      const visibilityRules = getFromSessionStore(
        SESSION_STORE_KEYS.visibilityRules,
      ) as VisibilityRules | undefined;
      if (!visibilityRules) return null;

      return visibilityRules[key];
    },
    [getFromSessionStore],
  );

  return {
    setVisibilityRule,
    getVisibilityRuleValue,
  };
}
